import { NavigateFunction } from "react-router";

import * as ROUTES from "src/platform/constants/routes";

/**
 * Navigation utilities for consistent routing throughout the application
 * Uses the centralized route definitions from platform/constants/routes
 */

interface NavigateOptions {
  replace?: boolean;
}

/**
 * Central navigation method that uses React Router's navigate
 * @param navigate React Router's navigate function
 * @param path Path to navigate to
 * @param options Optional navigation options
 */
export const navigateTo = (
  navigate: NavigateFunction,
  path: string,
  options?: NavigateOptions,
): void => {
  void navigate(path, options);
};

/**
 * Root and dashboard navigation
 */
export const goToRoot = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ROOT.path, options);

export const goToDashboard = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.DASHBOARD.path, options);

/**
 * Auth navigation
 */
export const goToLogin = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.AUTH.LOGIN.path, options);

export const goToRegister = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.AUTH.REGISTER.path, options);

/**
 * Organization navigation
 */
export const goToOrganizations = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ORGANIZATIONS.ROOT.path, options);

export const goToCreateOrganization = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ORGANIZATIONS.CREATE.path, options);

export const goToOrganizationDetails = (
  navigate: NavigateFunction,
  orgAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(navigate, ROUTES.ORGANIZATIONS.DETAILS.getPath(orgAlias), options);

export const goToOrganizationEdit = (
  navigate: NavigateFunction,
  orgAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(navigate, ROUTES.ORGANIZATIONS.EDIT.getPath(orgAlias), options);

export const goToOrganizationMembers = (
  navigate: NavigateFunction,
  orgAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(navigate, ROUTES.ORGANIZATIONS.MEMBERS.getPath(orgAlias), options);

export const goToOrganizationSettings = (
  navigate: NavigateFunction,
  orgAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.ORGANIZATIONS.ORG_SETTINGS.getPath(orgAlias),
    options,
  );

export const goToVcsConnections = (
  navigate: NavigateFunction,
  orgAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.ORGANIZATIONS.VCS_CONNECTIONS.getPath(orgAlias),
    options,
  );

/**
 * Project navigation
 */
export const goToProjects = (
  navigate: NavigateFunction,
  orgAlias?: string,
  options?: NavigateOptions,
): void => {
  if (orgAlias) {
    navigateTo(navigate, ROUTES.PROJECTS.LIST.getPath(orgAlias), options);
  } else {
    navigateTo(navigate, ROUTES.DASHBOARD.path, options);
  }
};

export const goToCreateProject = (
  navigate: NavigateFunction,
  orgAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(navigate, ROUTES.PROJECTS.CREATE.getPath(orgAlias), options);

export const goToProjectDetails = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.PROJECTS.DETAILS.getPath(orgAlias, projectAlias),
    options,
  );

export const goToProjectEdit = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.PROJECTS.EDIT.getPath(orgAlias, projectAlias),
    options,
  );

/**
 * User / Account navigation
 */
export const goToUserProfile = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ACCOUNT.PROFILE.path, options);

export const goToUserSettings = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ACCOUNT.SETTINGS.path, options);

export const goToAccountProfile = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ACCOUNT.PROFILE.path, options);

export const goToAccountSettings = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ACCOUNT.SETTINGS.path, options);

export const goToSettings = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.ACCOUNT.SETTINGS.path, options);

/**
 * Application navigation
 */
export const goToApplications = (
  navigate: NavigateFunction,
  options?: NavigateOptions,
): void => navigateTo(navigate, ROUTES.APPLICATIONS.ROOT.path, options);

export const goToApplicationDetails = (
  navigate: NavigateFunction,
  appAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(navigate, ROUTES.APPLICATIONS.DETAILS.getPath(appAlias), options);

export const goToProjectApplications = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.APPLICATIONS.PROJECT_APPLICATIONS.getPath(orgAlias, projectAlias),
    options,
  );

export const goToProjectApplicationDetails = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  appAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.APPLICATIONS.PROJECT_APPLICATION_DETAILS.getPath(
      orgAlias,
      projectAlias,
      appAlias,
    ),
    options,
  );

export const goToCreateApplication = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.APPLICATIONS.CREATE.getPath(orgAlias, projectAlias),
    options,
  );

/**
 * Stack navigation
 */
export const goToStackDetails = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  sysAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.STACKS.DETAILS.getPath(orgAlias, projectAlias, sysAlias),
    options,
  );

export const goToStackEdit = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  sysAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.STACKS.EDIT.getPath(orgAlias, projectAlias, sysAlias),
    options,
  );

export const goToAdoptStack = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.STACKS.ADOPT.getPath(orgAlias, projectAlias),
    options,
  );

export const goToStackServices = (
  navigate: NavigateFunction,
  orgAlias: string,
  projectAlias: string,
  sysAlias: string,
  options?: NavigateOptions,
): void =>
  navigateTo(
    navigate,
    ROUTES.STACKS.SERVICES.getPath(orgAlias, projectAlias, sysAlias),
    options,
  );
